/**
 * Scoped styles shared by the auth screens (login + set-password) so the
 * admin and client portals present the same dark, centered auth card.
 */
export const authStyles = /* css */ `
  .login-backdrop {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100dvh;
    padding: 1rem;
    /* Single dark-chrome surface shared with the portal sidebar/header
     * (design-system-spec §4.7) — no second dark value. */
    background: var(--color-header);
    overflow: hidden;
  }

  /* ── Card ── */
  .login-card {
    position: relative;
    width: 22rem;
    padding: 2.5rem 2rem;
    border-radius: var(--radius);
    background: var(--color-background);
    border: none;
    overflow: hidden;

    /* Smooth transitions for shrink */
    transition:
      width 0.25s cubic-bezier(0.4, 0, 0.2, 1),
      height 0.25s cubic-bezier(0.4, 0, 0.2, 1),
      padding 0.25s cubic-bezier(0.4, 0, 0.2, 1),
      border-radius 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Shrink to circle */
  .login-card.shrinking {
    width: 4rem;
    height: 4rem !important;
    padding: 0;
    border-radius: 2rem;
  }

  /* ── Form wrapper ── */
  .login-form-wrap {
    transition: opacity 0.125s ease, visibility 0.125s ease;
  }
  .login-form-wrap.visible {
    opacity: 1;
    visibility: visible;
  }
  .login-form-wrap.hidden {
    opacity: 0;
    visibility: hidden;
    position: absolute;
    pointer-events: none;
  }

  /* ── Typography ── */
  .login-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-foreground);
    margin: 0 0 1.5rem;
    text-align: center;
  }
  .login-subtitle {
    font-size: 0.8125rem;
    color: var(--color-muted-foreground);
    margin: -1rem 0 1.5rem;
    text-align: center;
  }

  /* ── Form elements ── */
  .login-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .login-field {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }
  /* These rules are injected unlayered, so they win over Tailwind's layered
   * utility classes by cascade-layer order — no !important needed. */
  .login-label {
    color: var(--color-muted-foreground);
    font-size: 0.8125rem;
  }
  .login-input {
    background: var(--color-background);
    border-color: var(--color-input);
    color: var(--color-foreground);
  }
  .login-input::placeholder {
    color: var(--color-muted-foreground);
  }
  .login-input:focus-visible {
    border-color: var(--color-ring);
  }
  .login-button {
    margin-top: 0.25rem;
    width: 100%;
  }
  .login-error {
    font-size: 0.8125rem;
    color: var(--color-destructive);
    margin: 0;
  }

  /* ── Spinner ── */
  .login-spinner-wrap {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .login-spinner {
    width: 1.5rem;
    height: 1.5rem;
    color: var(--color-primary);
    animation: login-spin 0.5s linear infinite;
  }
  @keyframes login-spin {
    to { transform: rotate(360deg); }
  }
`;
